// Vendor-verified item matching - shared logic per MATCHING_SPEC.md.
//
// This is the single source of truth that links a ClaimSoft item to its
// AccountEdge twin. It is intentionally pure (no Supabase calls) so the same
// functions back BOTH callers and can never drift:
//   1. The catalog import (handleImport in AdminItemCatalog.jsx).
//   2. The ordering engine (later).
//
// Identity of an item is the composite (source, item_number). The verified
// link between a ClaimSoft row and an AccountEdge row lives in the data
// (edge_item_number + match_* columns), not in the row identity, so
// re-imports never disturb a confirmed/manual mapping.
//
// Vendor-number column: standardized on `vendor_part_number` (the real column
// on the items table). Both ClaimSoft "VendorPartNumber" and AccountEdge
// "Vendor Item Number" import into it, and this module reads it.

// normalize(name): uppercase, strip everything except A-Z and 0-9.
export function normalizeVendor(name) {
  return (name || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

// Build the alias lookup once from vendor_aliases rows: { alias, canonical_name }.
// Keyed by the normalized alias so every vendor spelling resolves to one name.
export function buildAliasMap(aliasRows) {
  const map = new Map();
  for (const r of aliasRows || []) {
    const key = normalizeVendor(r.alias);
    if (key) map.set(key, r.canonical_name);
  }
  return map;
}

// resolveVendor(name) -> canonical | null.
// null means "this vendor spelling isn't in the alias table" - i.e. "can't
// verify". It is NOT an error; the decision rule in matchItem handles it.
export function resolveVendor(name, aliasMap) {
  const key = normalizeVendor(name);
  if (!key) return null;
  const canon = aliasMap.get(key);
  return canon == null ? null : canon;
}

// Build the AccountEdge index ONCE (not per item): two case-insensitive maps
// over the AccountEdge rows.
//   byItemNumber[UPPER(item_number)]        -> [ae rows]
//   byVendorItemNumber[UPPER(vendor_part_number)] -> [ae rows]
export function buildAeIndex(aeRows) {
  const byItemNumber = new Map();
  const byVendorItemNumber = new Map();
  for (const r of aeRows || []) {
    const k1 = (r.item_number || '').toUpperCase();
    if (k1) {
      if (!byItemNumber.has(k1)) byItemNumber.set(k1, []);
      byItemNumber.get(k1).push(r);
    }
    const k2 = (r.vendor_part_number || '').toUpperCase();
    if (k2) {
      if (!byVendorItemNumber.has(k2)) byVendorItemNumber.set(k2, []);
      byVendorItemNumber.get(k2).push(r);
    }
  }
  return { byItemNumber, byVendorItemNumber };
}

// matchItem(csItem, aeIndex, aliasMap) -> { aeItemNumber, method, status }
//
// status is returned in the storage form used by items.match_status:
//   'confirmed' | 'rejected' | 'unverified' | 'no_match'
//   (spec calls these CONFIRMED / REJECTED / UNVERIFIED / NO_MATCH)
// method matches items.match_method: 'direct' | 'via_vendor_number' | null.
// A fourth value, 'manual', exists in the column since 6 September 2026 and is
// written only by a person setting a link in the Item Catalog, never here.
//
// The decision rule rejects ONLY on positive vendor disagreement. Absence of
// vendor info never causes a rejection - it yields UNVERIFIED, because most
// number matches with an unknown vendor are still correct.
export function matchItem(csItem, aeIndex, aliasMap) {
  const key = (csItem.item_number || '').toUpperCase();
  const direct = key ? (aeIndex.byItemNumber.get(key) || []) : [];
  const viaVendor = key ? (aeIndex.byVendorItemNumber.get(key) || []) : [];

  // candidates = direct ++ via-vendor, deduped by row identity.
  const seen = new Set();
  const candidates = [];
  for (const c of [...direct, ...viaVendor]) {
    const id = c.id != null ? c.id : (c.source + ':' + c.item_number);
    if (seen.has(id)) continue;
    seen.add(id);
    candidates.push(c);
  }

  // method reflects how candidates were found: a direct number hit wins.
  const method = direct.length ? 'direct' : (viaVendor.length ? 'via_vendor_number' : null);

  if (candidates.length === 0) {
    return { aeItemNumber: null, method: null, status: 'no_match' };
  }

  const csCanon = resolveVendor(csItem.vendor, aliasMap);

  // 1. CONFIRMED - some candidate's vendor provably equals the CS vendor.
  const confirmed = candidates.find(c => {
    const cCanon = resolveVendor(c.vendor, aliasMap);
    return csCanon != null && cCanon != null && cCanon === csCanon;
  });
  if (confirmed) {
    return { aeItemNumber: confirmed.item_number, method, status: 'confirmed' };
  }

  // 2. REJECTED - CS vendor known, and EVERY candidate's vendor is known AND
  //    different. Cross-vendor collision: do NOT link.
  if (
    csCanon != null &&
    candidates.every(c => resolveVendor(c.vendor, aliasMap) != null) &&
    candidates.every(c => resolveVendor(c.vendor, aliasMap) !== csCanon)
  ) {
    return { aeItemNumber: null, method, status: 'rejected' };
  }

  // 3. UNVERIFIED - can't confirm, can't disprove (a vendor somewhere is null).
  //    Link the single best candidate (the direct-number one if present).
  const best = direct.length ? direct[0] : candidates[0];
  return { aeItemNumber: best.item_number, method, status: 'unverified' };
}
